import { useMemo } from 'react';
import { useDeals, Deal } from '@/hooks/useDeals';
import { useLeads } from '@/hooks/useLeads';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TempPill } from '@/components/leads/LeadVisual';
import {
  Phone,
  MessageCircle,
  AlertTriangle,
  Clock,
  DollarSign,
  Calendar,
} from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import { cn } from '@/lib/utils';

interface AtRiskDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AtRiskItem {
  deal: Deal;
  reasons: string[];
  daysStale: number;
  value: number;
}

export function AtRiskDrawer({ open, onOpenChange }: AtRiskDrawerProps) {
  const { data: deals } = useDeals();
  const { data: leads } = useLeads();

  const atRiskItems = useMemo(() => {
    if (!deals || !leads) return [];

    const now = new Date();
    const items: AtRiskItem[] = [];

    deals.forEach(deal => {
      if (deal.stage === 'won' || deal.stage === 'lost') return;

      const reasons: string[] = [];
      const lead = leads.find(l => l.id === deal.lead_id);

      // Check for overdue follow-up
      if (lead?.next_action_at && new Date(lead.next_action_at) < now) {
        reasons.push('Overdue follow-up');
      }

      // Check for stale activity (no update in 7+ days)
      const lastUpdate = deal.updated_at ? new Date(deal.updated_at) : new Date(deal.created_at);
      const daysStale = differenceInDays(now, lastUpdate);
      if (daysStale >= 7) {
        reasons.push(`No activity for ${daysStale} days`);
      }

      // Check for stuck in stage too long
      if (daysStale >= 14) {
        reasons.push('Stuck in stage 14+ days');
      }

      // Check for no next step scheduled
      if (!lead?.next_action_at && !lead?.next_action_type) {
        reasons.push('No next step scheduled');
      }

      if (reasons.length > 0) {
        items.push({
          deal,
          reasons,
          daysStale,
          value: deal.value || 0,
        });
      }
    });

    // Sort by value descending (highest risk value first)
    return items.sort((a, b) => b.value - a.value);
  }, [deals, leads]);

  const totalAtRiskValue = useMemo(() => 
    atRiskItems.reduce((sum, item) => sum + item.value, 0),
    [atRiskItems]
  );

  const handleWhatsApp = (deal: Deal) => {
    const lead = deal.lead;
    if (!lead?.phone) return;
    const cleanPhone = lead.phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Hi ${lead.name}, following up on our conversation.`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const handleCall = (deal: Deal) => {
    const lead = deal.lead;
    if (!lead?.phone) return;
    window.open(`tel:${lead.phone}`, '_self');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            At Risk Deals
          </SheetTitle>
          <SheetDescription>
            {atRiskItems.length} deals need attention • {formatCurrency(totalAtRiskValue)} at risk
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3">
          {atRiskItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-success" />
              </div>
              <p className="text-sm text-muted-foreground">
                No at-risk deals. Pipeline is healthy!
              </p>
            </div>
          ) : (
            atRiskItems.map((item) => (
              <Card key={item.deal.id} className="border-destructive/20">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium truncate">
                          {item.deal.lead?.name || 'Unknown'}
                        </p>
                        {item.deal.lead?.temperature && (
                          <TempPill value={item.deal.lead.temperature as any} />
                        )}
                      </div>
                      
                      {item.deal.title && (
                        <p className="text-xs text-muted-foreground truncate mb-2">
                          {item.deal.title}
                        </p>
                      )}

                      {/* Value */}
                      <div className="flex items-center gap-3 mb-2">
                        {item.deal.value ? (
                          <div className="flex items-center gap-1 text-sm font-medium text-primary">
                            <DollarSign className="w-3.5 h-3.5" />
                            {item.deal.value.toLocaleString()} {item.deal.currency}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">No value</span>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {item.deal.stage}
                        </Badge>
                      </div>

                      {/* Risk Reasons */}
                      <div className="flex flex-wrap gap-1">
                        {item.reasons.map((reason, i) => (
                          <span
                            key={i}
                            className="text-[10px] bg-destructive/10 text-destructive px-2 py-0.5 rounded-full"
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary hover:border-primary/30"
                        onClick={() => handleCall(item.deal)}
                        disabled={!item.deal.lead?.phone}
                      >
                        <Phone className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 hover:bg-[#25D366]/10 hover:text-[#25D366] hover:border-[#25D366]/30"
                        onClick={() => handleWhatsApp(item.deal)}
                        disabled={!item.deal.lead?.phone}
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
